// workflowButtonConfig.ts
import React from 'react';
import {
  CheckOutlined,
  PlayCircleOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  FileDoneOutlined,
  CheckCircleOutlined,
  InboxOutlined,
  DollarOutlined,
  ExperimentOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';

export interface WorkflowButtonConfig {
  label: string;
  type: 'primary' | 'default' | 'ghost' | 'dashed' | 'link' | 'text';
  icon?: React.ReactNode;
  danger?: boolean;
  variant?: 'success' | 'warning' | 'info' | 'danger';
}

export const WORKFLOW_BUTTON_CONFIGS: Record<string, WorkflowButtonConfig> = {
  // Standard Test Flow
  DRAFT_TO_PENDING_TA: {
    label: 'Confirm',
    type: 'primary',
    icon: React.createElement(CheckOutlined),
    variant: 'success',
  },
  DRAFT_TO_PENDING_VA: {
    label: 'Confirm',
    type: 'primary',
    icon: React.createElement(CheckOutlined),
    variant: 'success',
  },
  PENDING_TA_TO_APPROVED: {
    label: 'Approve',
    type: 'primary',
    icon: React.createElement(CheckOutlined),
    variant: 'success',
  },
  PENDING_VA_TO_APPROVED: {
    label: 'Approve',
    type: 'primary',
    icon: React.createElement(CheckOutlined),
    variant: 'success',
  },
  PENDING_TA_TO_PENDING_PAYMENT: {
    label: 'Approve',
    type: 'primary',
    icon: React.createElement(DollarOutlined),
    variant: 'success',
  },
  PENDING_PAYMENT_TO_PENDING_ITR: {
    label: '',
    type: 'primary',
    icon: React.createElement(ExperimentOutlined),
    variant: 'info',
  },
  PENDING_PAYMENT_TO_PENDING_SV: {
    label: '',
    type: 'primary',
    icon: React.createElement(EnvironmentOutlined),
    variant: 'info',
  },
  PENDING_SV_TO_PENDING_ITR: {
    label: 'Complete Site Visit',
    type: 'primary',
    icon: React.createElement(ExperimentOutlined),
    variant: 'info',
  },
  PENDING_TR_TO_PENDING_TRA: {
    label: 'Approve',
    type: 'primary',
    icon: React.createElement(FileTextOutlined),
    variant: 'info',
  },
  PENDING_MANUFACTURING_TO_PENDING_APPROVAL: {
    label: 'Submit for Approval',
    type: 'primary',
    icon: React.createElement(FileTextOutlined),
    variant: 'info',
  },

  PENDING_TRA_TO_PENDING_TR: {
    label: 'Reject',
    type: 'primary',
    icon: React.createElement(FileTextOutlined),
    variant: 'info',
  },

  PENDING_APPROVAL_TO_PENDING_MANUFACTURING: {
    label: 'Reject',
    type: 'primary',
    icon: React.createElement(FileTextOutlined),
    variant: 'info',
  },

  PENDING_TRA_TO_TR_APPROVED: {
    label: 'Approve Results',
    type: 'primary',
    icon: React.createElement(FileTextOutlined),
    variant: 'info',
  },

  PENDING_APPROVAL_TO_PENDING_COLLECTION: {
    label: 'Submit for Collection',
    type: 'primary',
    icon: React.createElement(SafetyCertificateOutlined),
    variant: 'success',
  },

  PENDING_ITRA_TO_PENDING_FRS: {
    label: 'Approve Initial Results',
    type: 'primary',
    icon: React.createElement(SafetyCertificateOutlined),
    variant: 'success',
  },

  TR_APPROVED_TO_PENDING_SUBMISSION: {
    label: 'Submit For Final Submission',
    type: 'primary',
    icon: React.createElement(SafetyCertificateOutlined),
    variant: 'success',
  },

  PENDING_SUBMISSION_TO_PENDING_APPROVAL: {
    label: 'Submit for Final Approval',
    type: 'primary',
    icon: React.createElement(SafetyCertificateOutlined),
    variant: 'success',
  },

  PENDING_FRS_TO_PENDING_FA: {
    label: 'Submit Final Report',
    type: 'primary',
    icon: React.createElement(FileDoneOutlined),
    variant: 'info',
  },
  PENDING_APPROVAL_TO_DONE: {
    label: 'Approve Final Results',
    type: 'primary',
    icon: React.createElement(CheckCircleOutlined),
    variant: 'success',
  },

  PENDING_COLLECTION_TO_COLLECTED: {
    label: 'Mark Collected',
    type: 'primary',
    icon: React.createElement(CheckCircleOutlined),
    variant: 'success',
  },

  // Vaccine Manufacturing Flow
  DRAFT_TO_PENDING_MA: {
    label: 'Submit for Manufacturing Approval',
    type: 'primary',
    icon: React.createElement(CheckOutlined),
    variant: 'success',
  },
  PENDING_MA_TO_PENDING_PAYMENT: {
    label: 'Approve Manufacturing & Send for Payment',
    type: 'primary',
    icon: React.createElement(DollarOutlined),

    variant: 'success',
  },
  PENDING_PAYMENT_TO_PENDING_PRODUCTION: {
    label: 'Payment Received - Start Production',
    type: 'primary',
    icon: React.createElement(PlayCircleOutlined),
    variant: 'info',
  },
  PENDING_PRODUCTION_TO_PENDING_FA: {
    label: 'Production Complete - Submit for Approval',
    type: 'primary',
    icon: React.createElement(SafetyCertificateOutlined),

    variant: 'info',
  },
  PENDING_FA_TO_PENDING_COLLECTION: {
    label: 'Approve & Ready for Collection',
    type: 'primary',
    icon: React.createElement(InboxOutlined),
    variant: 'success',
  },

  // Direct Sales Flow
  DRAFT_TO_PENDING_PA: {
    label: 'Submit for Initial Approval',
    type: 'primary',
    icon: React.createElement(CheckOutlined),

    variant: 'success',
  },
  PENDING_IA_TO_PENDING_PAYMENT: {
    label: 'Approve & Send for Payment',
    type: 'primary',
    icon: React.createElement(DollarOutlined),

    variant: 'success',
  },
  PENDING_PA_TO_APPROVED: {
    label: 'Approve',
    type: 'primary',
    icon: React.createElement(CheckOutlined),

    variant: 'success',
  },

  PENDING_PAYMENT_TO_PENDING_COLLECTION: {
    label: 'Payment Received - Ready for Collection',
    type: 'primary',
    icon: React.createElement(InboxOutlined),

    variant: 'info',
  },

  APPROVED_TO_PENDING_COLLECTION: {
    label: 'Submit for Pending Collection',
    type: 'primary',
    icon: React.createElement(InboxOutlined),

    variant: 'info',
  },

  // Common Actions
  PENDING_COLLECTION_TO_DONE: {
    label: 'Mark as Collected & Complete',
    type: 'primary',
    icon: React.createElement(CheckCircleOutlined),

    variant: 'success',
  },
};

// Helper function to get button configuration
export const getWorkflowButtonConfig = (action: string): WorkflowButtonConfig => {
  return (
    WORKFLOW_BUTTON_CONFIGS[action] || {
      label: formatActionLabel(action),
      type: 'default',
      icon: React.createElement(CheckOutlined),
      variant: 'info',
    }
  );
};

// Helper function to format action labels for unknown actions
const formatActionLabel = (action: string): string => {
  return action
    .replace(/_TO_/g, ' → ')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (l) => l.toUpperCase());
};

export {
  CheckOutlined,
  PlayCircleOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  FileDoneOutlined,
  CheckCircleOutlined,
  InboxOutlined,
  DollarOutlined,
  ExperimentOutlined,
  SafetyCertificateOutlined,
};
